import { ReadDialog } from "@/components/ReadDialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useUserContext } from "@/contexts/user";
import { useToast } from "@/hooks/use-toast";
import { forwardRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";

const text = `## *Så använder du ${import.meta.env.VITE_APP_NAME}*
*(och varför den funkar)*

Det här är inte en app du ska ta dig igenom.
Det är en livsstil du kliver in i, ett steg i taget.

${import.meta.env.VITE_APP_NAME} följer boken *Konsten att göra det möjliga möjligt*. Du kan använda dem tillsammans eller var för sig. Poängen är inte att skynda fram, utan att låta det landa.

När du har gått igenom alla tre steg börjar du om.
Med nya frågor. Nya perspektiv.

Varje ny loop fördjupar din självmedvetenhet och din utveckling.
Du lär dig att relatera till din omgivning.

Att våga vara du, i alla lägen.
Att veta vad du vill, varför och hur du tar dig an ditt liv.
Det blir inte något du gör ibland.
Det blir det nya normala.

## *Ett steg i taget*

Resan är uppdelad i tre steg. Varje steg har kapitel med tydliga teman.
Du behöver fullfölja varje rubrik innan du går vidare.

Inte för att prestera, utan för träna på att det här ska bli din livsstil.

## *Börja med Intro*

Varje steg börjar med ett Intro du kan lyssna på.
Det ger riktning och sammanhang.

## *Din resa i ${import.meta.env.VITE_APP_NAME}*

**Läs eller Lyssna:** välj det som funkar för dig just nu.

**Lära:** Du förstår temat och börjar arbeta med din egen verklighet, så att det går från teori till praktik och blir en del av din vardag.

**Experiment:** Experimenten hjälper dig att testa i vardagen
och träna den experimentella livsstilen.

**Reflektion:** Hjälper dig att se mönster, få insikter och justera riktning.

**Tema Practise:** Efter varje rubrik finns en Practise kopplad till temat. Syftet är att släppa det som håller dig tillbaka och utveckla mer närvaro, kreativitet och frihet.

## *Följ din progress*
Du ser hur långt du har kommit. Din balans just nu. Dina dagar du har jobbat med appen i följd.

## *Dagens Dare*
Starta dagen med att inspireras av Dagens Dare. Olika teman varje dag.

## *Skatta din balans*
Hur mår du just nu, emotionellt, fysiskt och mentalt?

Om du vill, starta dagen med en kort meditation som finns under skatta din balans morgon.
Skatta din balans från 1–10:
Hur känns det i dig just nu?

Avsluta dagen med en kvällsmeditation och gör samma skattning igen.
Det hjälper dig att följa din utveckling och skapa mer medvetenhet, ett steg i taget.

## *Viktigt att veta*

Det finns inget “rätt” tempo

Du behöver inte göra allt perfekt

Appen är ett stöd, en mjuk påminnelse om att du är värdefull

Du tränar på att ta ägarskap över ditt liv, ditt agerande och din riktning

*Ett steg i taget. Vi finns här.*`;

const HelloSection = forwardRef<HTMLElement, { name: string }>(function ({ name }, ref) {
    const [showOnboarding, setShowOnboarding] = useState(false);
    const { profile, updateProfile } = useUserContext();
    const { toast } = useToast();

    useEffect(() => {
        if (!profile?.show_onboarding) return;

        setShowOnboarding(true);
        (async () => {
            const success = updateProfile({
                show_onboarding: false,
            });
            if (!success) {
                toast({
                    title: `Ett oväntat fel har uppstått.`,
                    variant: "destructive"
                });
            }
        })();
    }, [profile?.show_onboarding, toast, updateProfile]);

    return (
        <section
            className="pt-16 bg-muted/30 items-center flex flex-col justify-center gap-4"
            ref={ref}
        >
            <div className="text-2xl font-bold">
                Hej {name}!
            </div>
            <div className="flex flex-col items-center">
                <ReadDialog
                    open={showOnboarding}
                    onOpenChange={setShowOnboarding}
                    textColor={"text-primary"}
                    scrollContainerClass="scrollbar-thumb-primary"
                    text={text}
                    title="Hur gör man..."
                    suppressProviderWarnings={true}
                />
                <Button onClick={() => setShowOnboarding(true)}>Kom igång</Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant={"default"}
                            className="rounded-full w-24 h-24 text-md m-8 flex flex-col"
                        >
                            Köp<br />boken
                        </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent className="w-48">
                        <DropdownMenuItem className="text-md" asChild>
                            <Link
                                className="flex flex-row gap-2 "
                                to={"https://www.adlibris.com/sv/bok/konsten-att-gora-det-mojliga-mojligt-en-livsstil-9789153133094"}
                                target="_blank"
                            >
                                <img src="https://www.adlibris.com/favicon.ico" className="size-6"></img>
                                Adlibris
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-md" asChild>
                            <Link
                                className="flex flex-row gap-2"
                                to={"https://www.amazon.se/Konsten-att-g%C3%B6ra-m%C3%B6jliga-m%C3%B6jligt/dp/9153133099"}
                                target="_blank"
                            >
                                <img src="https://www.amazon.se/favicon.ico" className="size-6"></img>
                                Amazon
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-md" asChild>
                            <Link
                                className="flex flex-row gap-2"
                                to={"https://www.bokus.com/bok/9789153133094/konsten-att-gora-det-mojliga-mojligt-en-livsstil"}
                                target="_blank"
                            >
                                <img src="https://www.bokus.com/favicon.ico" className="size-6"></img>
                                Bokus
                            </Link>
                        </DropdownMenuItem>

                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </section>
    );
});

export { HelloSection };